import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import * as Accordion from "@radix-ui/react-accordion";
import { ArrowRight, ChevronDown, Flower2, Truck, Leaf, Palette, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import SubscribeDialog from "@/components/SubscribeDialog";
import { TIERS } from "@/lib/api";
import { T } from "@/lib/testIds";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay = 0, size = "text-[13vw] sm:text-[10vw]" }) {
  return (
    <span className="reveal-mask block">
      <motion.span
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, ease: EASE, delay }}
        className={`font-serif font-light leading-[0.92] tracking-[-0.02em] ${size}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

function FadeUp({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [freqByTier, setFreqByTier] = useState({ bud: "weekly", bloom: "weekly", signature: "weekly" });
  const [selectedTier, setSelectedTier] = useState("bloom");
  const [selectedFreq, setSelectedFreq] = useState("weekly");

  const heroRef = useRef(null);
  const { scrollYProgress: heroProg } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(heroProg, [0, 1], ["0%", "22%"]);
  const heroImgScale = useTransform(heroProg, [0, 1], [1, 1.12]);

  const openSubscribe = (tierId) => {
    setSelectedTier(tierId);
    setSelectedFreq(freqByTier[tierId]);
    setDialogOpen(true);
  };

  return (
    <div className="grain">
      {/* NAV */}
      <header className="fixed top-0 left-0 right-0 z-40 mix-blend-difference text-[var(--bg)]">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
          <a href="#top" data-testid={T.nav.logo} className="font-serif text-2xl leading-none tracking-tight">BUDS<span className="italic font-light">.</span></a>
          <nav className="hidden md:flex items-center gap-10 text-[11px] tracking-[0.24em] uppercase">
            <a href="#memberships" data-testid={T.nav.memberships}>Memberships</a>
            <a href="#manifesto" data-testid={T.nav.manifesto}>Manifesto</a>
            <a href="#faq" data-testid={T.nav.faq}>FAQ</a>
            <Link to="/admin" data-testid={T.nav.admin}>Studio Admin</Link>
          </nav>
          <button onClick={() => openSubscribe("bloom")} className="text-[11px] tracking-[0.24em] uppercase border border-current px-4 py-2">
            Join
          </button>
        </div>
      </header>

      {/* HERO */}
      <section id="top" ref={heroRef} data-testid={T.hero.root} className="relative min-h-[100svh] pt-32 sm:pt-40 pb-16 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
          {/* Left kinetic text */}
          <div className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.9 }}
              className="text-[10px] tracking-[0.32em] uppercase text-[var(--sage-deep)] mb-8"
            >
              Bangalore · Est. Floral Studio · Ind.
            </motion.p>
            <h1 className="font-serif">
              <MaskedLine delay={0.1} size="text-[13vw] sm:text-[9vw] lg:text-[8.2vw]">BUDS</MaskedLine>
              <MaskedLine delay={0.25} size="text-[13vw] sm:text-[9vw] lg:text-[8.2vw]"><em className="italic font-light">Floral</em> Club</MaskedLine>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: EASE }}
              className="mt-10 max-w-md"
            >
              <p className="font-serif italic text-2xl sm:text-3xl leading-tight text-[var(--ink)] mb-4">Beautiful flowers. Every week.</p>
              <p className="text-[var(--ink-soft)] leading-relaxed text-[15px]">
                Bring fresh, thoughtfully curated flowers into your home or workspace with flexible weekly or fortnightly deliveries. Every arrangement is handcrafted using the finest seasonal flowers available.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-6">
                <button data-testid={T.hero.cta} onClick={() => openSubscribe("bloom")} className="btn-primary">Choose Your Membership <ArrowRight size={14} /></button>
                <a data-testid={T.hero.secondary} href="#manifesto" className="btn-ghost">Learn More</a>
              </div>
            </motion.div>
          </div>

          {/* Right clipped-frame parallax */}
          <div className="lg:col-span-5 relative min-h-[520px] sm:min-h-[640px] lg:min-h-[680px]">
            <motion.div
              initial={{ opacity: 0, scaleY: 0.85 }} animate={{ opacity: 1, scaleY: 1 }} transition={{ duration: 1.3, ease: EASE, delay: 0.3 }}
              style={{ transformOrigin: "top center" }}
              className="absolute inset-y-0 right-0 w-full overflow-hidden"
            >
              <motion.img
                src="https://images.unsplash.com/photo-1487530811176-3780de880c2d?auto=format&fit=crop&w=1600&q=80"
                alt="Editorial bouquet"
                style={{ y: heroImgY, scale: heroImgScale }}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between text-[var(--bg)] text-[10px] tracking-[0.24em] uppercase">
                <span>Fig. 01</span>
                <span>Ranunculus · Peonies</span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 left-6 sm:left-10 flex items-center gap-4 text-[10px] tracking-[0.24em] uppercase text-[var(--ink-soft)]"
        >
          <span className="w-16 h-[1px] bg-[var(--line-strong)]" />
          Scroll to explore
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[var(--line)] py-6 bg-[var(--bg-alt)]">
        <Marquee gradient={false} speed={22} pauseOnHover={false}>
          {["Seasonal", "Handcrafted", "Delivered Fresh", "Bengaluru Studio", "Imported Stems", "Weekly Ritual"].map((w, i) => (
            <span key={i} className="marquee-item flex items-center gap-32">
              {w}
              <span className="text-[var(--clay)]">✽</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MEMBERSHIPS */}
      <section id="memberships" className="relative py-24 sm:py-32">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-4">
              <p className="chapter-num text-6xl mb-4">01</p>
              <FadeUp><h2 className="font-serif text-5xl sm:text-6xl leading-[0.95]">Memberships,<br/><em className="italic font-light">deliberately curated.</em></h2></FadeUp>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <FadeUp delay={0.1}>
                <p className="text-[var(--ink-soft)] leading-relaxed max-w-md">
                  Three memberships, each an ongoing dialogue between our florists and your home. Choose weekly for a fresh ritual, or fortnightly for a slower cadence.
                </p>
              </FadeUp>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {TIERS.map((tier, i) => {
              const isSignature = tier.id === "signature";
              const freq = freqByTier[tier.id];
              const monthly = freq === "weekly" ? tier.price * 4 : tier.price * 2;
              return (
                <FadeUp key={tier.id} delay={i * 0.08}>
                  <article
                    data-testid={T.tier(tier.id)}
                    className={`group relative flex flex-col h-full border ${isSignature ? "border-[var(--ink)]" : "border-[var(--line-strong)]"}`}
                    style={{ background: isSignature ? "var(--ink)" : "transparent", color: isSignature ? "var(--bg)" : "var(--ink)" }}
                  >
                    <div className="relative overflow-hidden aspect-[4/5]">
                      <img
                        src={tier.image}
                        alt={tier.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                      />
                      {tier.badge && (
                        <span className="absolute top-4 left-4 bg-[var(--bg)] text-[var(--ink)] text-[10px] tracking-[0.22em] uppercase px-3 py-1.5">
                          {tier.badge}
                        </span>
                      )}
                    </div>
                    <div className="p-6 sm:p-8 flex flex-col flex-1">
                      <div className="flex items-baseline justify-between mb-1">
                        <h3 className="font-serif text-3xl leading-none">{tier.name}</h3>
                        <span className="text-[10px] tracking-[0.22em] uppercase opacity-70">0{i+1}</span>
                      </div>
                      <p className="font-serif text-xl mt-2 mb-4">₹{tier.price.toLocaleString("en-IN")} <span className="text-[11px] tracking-widest uppercase opacity-70">/ week</span></p>
                      <p className="text-sm leading-relaxed opacity-80 mb-6">{tier.tagline}</p>

                      <ul className="space-y-2.5 text-sm mb-8">
                        {tier.features.map((f) => (
                          <li key={f} className="flex gap-3">
                            <span className="mt-2 inline-block w-3 h-[1px] opacity-60" style={{ background: isSignature ? "var(--bg)" : "var(--ink)" }} />
                            <span className="opacity-90">{f}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-auto">
                        <p className="text-[10px] tracking-[0.24em] uppercase opacity-70 mb-2">Frequency</p>
                        <div className="grid grid-cols-2 gap-1 mb-4">
                          {["weekly", "fortnightly"].map((f) => (
                            <button
                              key={f}
                              data-testid={T.frequency(tier.id, f)}
                              onClick={() => setFreqByTier((s) => ({ ...s, [tier.id]: f }))}
                              className="text-[11px] tracking-[0.2em] uppercase py-2.5 border transition-colors duration-500"
                              style={{
                                borderColor: freq === f
                                  ? (isSignature ? "var(--bg)" : "var(--ink)")
                                  : (isSignature ? "rgba(245,241,234,0.35)" : "var(--line-strong)"),
                                background: freq === f ? (isSignature ? "var(--bg)" : "var(--ink)") : "transparent",
                                color: freq === f ? (isSignature ? "var(--ink)" : "var(--bg)") : "inherit",
                              }}
                            >
                              {f}
                            </button>
                          ))}
                        </div>
                        <p className="text-[11px] tracking-widest uppercase opacity-70 mb-6">
                          Approx ₹{monthly.toLocaleString("en-IN")} / month
                        </p>

                        <button
                          data-testid={T.tierSubscribe(tier.id)}
                          onClick={() => openSubscribe(tier.id)}
                          className="w-full flex items-center justify-between border py-4 px-5 text-[11px] tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-[var(--ink)] hover:text-[var(--bg)]"
                          style={{
                            borderColor: isSignature ? "var(--bg)" : "var(--ink)",
                            color: isSignature ? "var(--bg)" : "var(--ink)",
                          }}
                        >
                          <span>{tier.cta}</span>
                          <ArrowRight size={14} />
                        </button>
                      </div>
                    </div>
                  </article>
                </FadeUp>
              );
            })}
          </div>

          {/* Reference imagery note */}
          <FadeUp delay={0.15}>
            <div className="mt-10 flex items-start gap-4 max-w-2xl mx-auto text-center sm:text-left border border-[var(--line)] px-6 py-5" data-testid="memberships-reference-note">
              <span className="font-serif italic text-[var(--sage-deep)] text-xl leading-none mt-0.5">*</span>
              <p className="text-[13px] text-[var(--ink-soft)] leading-relaxed">
                Images shown in the memberships are references only. The actual flowers you receive
                may vary according to season and availability — every arrangement is handcrafted
                from the finest stems at their peak that week.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>
      <section id="manifesto" className="py-24 sm:py-36 bg-[var(--bg-alt)]">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
          <p className="chapter-num text-6xl mb-4">02</p>
          <FadeUp>
            <h2 className="font-serif text-5xl sm:text-7xl leading-[0.95] max-w-4xl">
              A weekly ritual, <em className="italic font-light">softly staged</em> across your home.
            </h2>
          </FadeUp>

          <div className="mt-20 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4 relative min-h-[420px]">
              <FadeUp>
                <div className="relative overflow-hidden aspect-[3/4]">
                  <img
                    src="https://images.unsplash.com/photo-1629511617783-dc852a6e3768?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                    alt="Studio florist"
                    className="w-full h-full object-cover"
                  />
                </div>
              </FadeUp>
            </div>

            <div className="lg:col-span-7 lg:col-start-6 space-y-16">
              {[
                { n: "i.", title: "Handcrafted, never assembled.", body: "Every arrangement is composed by hand in our Bangalore studio — one stem at a time, considered for form, shadow and season." },
                { n: "ii.", title: "Sourced with restraint.", body: "We work with a small circle of Indian growers, and — for The Signature — a curated import list that brings peonies, ranunculus and garden roses to your table." },
                { n: "iii.", title: "A slow, editorial cadence.", body: "Weekly or fortnightly. Never both at once. Pause anytime with 48 hours' notice — a floral membership that respects the pace of your week." },
              ].map((c, i) => (
                <FadeUp key={c.n} delay={i * 0.1}>
                  <div className="grid grid-cols-12 gap-6 items-start">
                    <span className="col-span-2 font-serif italic text-2xl text-[var(--sage-deep)]">{c.n}</span>
                    <div className="col-span-10">
                      <h3 className="font-serif text-2xl sm:text-3xl leading-tight mb-3">{c.title}</h3>
                      <p className="text-[var(--ink-soft)] leading-relaxed max-w-lg">{c.body}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-6">
              <p className="chapter-num text-6xl mb-4">03</p>
              <FadeUp><h2 className="font-serif text-5xl sm:text-6xl leading-[0.95]">What is <em className="italic font-light">included.</em></h2></FadeUp>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-px bg-[var(--line-strong)]">
            {[
              { Icon: Flower2, t: "Handcrafted arrangements" },
              { Icon: Truck, t: "Complimentary delivery" },
              { Icon: Leaf, t: "Fresh seasonal flowers" },
              { Icon: Palette, t: "Unique designs every week" },
              { Icon: Sparkles, t: "Premium presentation" },
            ].map(({ Icon, t }, i) => (
              <FadeUp key={t} delay={i * 0.06} className="bg-[var(--bg)]">
                <div className="p-8 sm:p-10 h-full flex flex-col justify-between min-h-[220px]">
                  <Icon strokeWidth={1} size={30} className="text-[var(--sage-deep)]" />
                  <p className="font-serif text-2xl leading-tight mt-8">{t}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 sm:py-32 bg-[var(--ink)] text-[var(--bg)]">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
          <p className="chapter-num text-6xl mb-4 text-[var(--blush)]">04</p>
          <FadeUp><h2 className="font-serif text-5xl sm:text-6xl leading-[0.95] mb-16">How it <em className="italic font-light">works.</em></h2></FadeUp>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              "Choose your membership",
              "Select weekly or fortnightly",
              "We handcraft your flowers",
              "Delivered fresh to your doorstep",
            ].map((step, i) => (
              <FadeUp key={step} delay={i * 0.1}>
                <div className="border-t border-[var(--bg)]/25 pt-6">
                  <p className="chapter-num text-4xl text-[var(--blush)]">0{i+1}</p>
                  <p className="font-serif text-2xl leading-tight mt-10 max-w-[220px]">{step}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* IMPORTANT NOTES */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10">
          <FadeUp>
            <div className="border border-[var(--line-strong)] p-8 sm:p-14 bg-[var(--bg-alt)]">
              <p className="chapter-num text-4xl mb-2">05</p>
              <h2 className="font-serif text-3xl sm:text-4xl mb-8">A few honest notes.</h2>
              <ul className="space-y-4 text-[15px] leading-relaxed">
                {[
                  "Subscriptions renew automatically until cancelled.",
                  "Deliveries may be paused or rescheduled with a minimum of 48 hours' notice.",
                  "Vases are not included in any membership tier.",
                  "Flower varieties change seasonally depending on freshness and availability.",
                  "The Signature Membership includes imported stems whenever available.",
                ].map((n) => (
                  <li key={n} className="flex gap-4">
                    <span className="font-serif italic text-[var(--sage-deep)] mt-1">—</span>
                    <span className="text-[var(--ink)]">{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 sm:py-32 bg-[var(--bg-alt)]">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10">
          <p className="chapter-num text-6xl mb-4">06</p>
          <FadeUp><h2 className="font-serif text-5xl sm:text-6xl leading-[0.95] mb-16">Questions, <em className="italic font-light">answered.</em></h2></FadeUp>

          <Accordion.Root type="single" collapsible className="border-t border-[var(--line-strong)]">
            {[
              ["Can I skip a delivery?", "Yes. Log in or contact the studio with at least 48 hours' notice and we'll skip your next delivery without any charge."],
              ["Can I change my membership?", "Absolutely. Upgrade or downgrade at any time from your account or by writing to us. Changes take effect from the next delivery cycle."],
              ["Can I cancel anytime?", "Yes. There is no lock-in. Cancel from your account and your final delivery will be honoured before closure."],
              ["Do you deliver across Bangalore?", "We deliver across all Bangalore pincodes. For deliveries outside the city, please contact the studio directly."],
              ["Will I receive the same flowers every week?", "No. Every arrangement is composed uniquely, using what is at peak season and freshness that week."],
              ["Are flowers seasonal?", "Yes. Our sourcing is dictated by the season and availability. This is what keeps every arrangement genuinely fresh."],
            ].map(([q, a]) => (
              <Accordion.Item key={q} value={q} className="border-b border-[var(--line-strong)]">
                <Accordion.Header>
                  <Accordion.Trigger className="group w-full flex items-center justify-between text-left py-8 gap-6">
                    <span className="font-serif text-2xl sm:text-3xl leading-tight">{q}</span>
                    <ChevronDown size={20} className="shrink-0 transition-transform duration-500 group-data-[state=open]:rotate-180" />
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className="acc-content overflow-hidden">
                  <p className="pb-8 pr-16 text-[var(--ink-soft)] leading-relaxed max-w-2xl">{a}</p>
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </div>
      </section>

      {/* CTA + FOOTER */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 text-center">
          <FadeUp>
            <p className="chapter-num text-4xl mb-4">07</p>
            <h2 className="font-serif text-5xl sm:text-7xl leading-[0.95] mb-10">
              Begin your <em className="italic font-light">weekly ritual.</em>
            </h2>
            <button onClick={() => openSubscribe("bloom")} className="btn-primary">Choose Your Membership <ArrowRight size={14} /></button>
          </FadeUp>
        </div>
      </section>

      <footer className="border-t border-[var(--line-strong)] py-10">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[10px] tracking-[0.24em] uppercase text-[var(--ink-soft)]">
          <span>© BUDS Floral Club · Bangalore</span>
          <span>A weekly ritual, softly staged.</span>
        </div>
      </footer>

      <SubscribeDialog open={dialogOpen} onOpenChange={setDialogOpen} initialTier={selectedTier} initialFrequency={selectedFreq} />
    </div>
  );
}
